import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import SpeakerImage from "../../../../images/speaker.png";
import SkeletonTable from "../../../Common/SkeletonTable";
import ErrorCard from "../../../Common/ErrorCard";
import EmptyCard from "../../../Common/EmptyCard";
import PaginationBar from "../../../Common/PaginationBar";
import { inventoryFetcher } from "../../../../network/InventoryService";
import { getInventoryTypeTitle } from "../../../../../../utilities/helpers";
import { DEFAULT_PAGE_SIZE } from "../../../../utils/inventoryHelper";

const InventoryTable = ({
  searchText,
  setShowInventoryCreateModal,
  onInventorySelect,
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchText) {
      params.append("search_term", searchText);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-model?${params.toString()}`;
  }, [page, searchText]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const inventoryData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={9}
          rowCount={7}
          headers={[
            "#",
            "Inventory Image",
            "Inventory Type ID",
            "Inventory Name",
            "Brand Name",
            "Inventory Type",
            "Model Name",
            "Status",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading inventory"
          message="Failed to load inventory data. Please try again."
        />
      );
    }

    if (inventoryData.length === 0) {
      return (
        <EmptyCard
          title="No inventory found"
          message={
            searchText
              ? "No inventory found matching your search criteria."
              : "No inventory data available. Create your first inventory item."
          }
        />
      );
    }

    return (
      <table className="inventory-table device-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Inventory Image</th>
            <th>Inventory Type ID</th>
            <th>Inventory Name</th>
            <th>Brand Name</th>
            <th>Inventory Type</th>
            <th>Model Name</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {inventoryData.map((inventory, index) => (
            <tr key={inventory.id || index} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td>
                <div className="inventory-image-container">
                  <img
                    src={
                      inventory.images && inventory.images[0]
                        ? inventory.images[0]
                        : SpeakerImage
                    }
                    alt={inventory.name}
                    className="inventory-image"
                  />
                </div>
              </td>
              <td>{inventory.unique_id || inventory.id}</td>
              <td title={inventory.name || ""}>{inventory.name || "-"}</td>
              <td>{inventory.brand || "-"}</td>
              <td>{getInventoryTypeTitle(inventory.type)}</td>
              <td title={inventory.model || ""}>{inventory.model || "-"}</td>
              <td>
                <span
                  className={`inventory-status-badge ${
                    inventory.is_active ? "active" : "inactive"
                  }`}
                >
                  {inventory.is_active ? "Active" : "Deactivated"}
                </span>
              </td>
              <td>
                <button
                  className="details-btn"
                  onClick={() => onInventorySelect(inventory)}
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </>
  );
};

export default InventoryTable;
